import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConsentLayout } from './ConsentLayout';
import { getConsentDraft } from '../../../lib/consent/consentDraft';

/**
 * Step 3 — holding screen on the child's device after Step 2.
 *
 * The parent has just received the invite email. The child has
 * nothing more to do — their browser can stay on this screen, or
 * they can close the tab; the next action is the parent clicking
 * the link on their own device, which routes to Step 4.
 *
 * No timer / polling — when the parent commits at Step 7 (Wave 3.3),
 * the child can sign in normally with the credentials sent to them
 * via a separate setup-password email at that point.
 */
export function PendingInvite() {
  const navigate = useNavigate();

  // Guard: don't show if there's no draft (someone hit the URL directly).
  useEffect(() => {
    const draft = getConsentDraft();
    if (!draft?.parentEmail) {
      navigate('/parent/consent/dob', { replace: true });
    }
  }, [navigate]);

  const draft = getConsentDraft();
  const maskedEmail = draft?.parentEmail ? maskEmail(draft.parentEmail) : 'your parent';

  return (
    <ConsentLayout
      step={3}
      title="Email sent"
      lead={
        <>
          We sent a link to <strong>{maskedEmail}</strong>. Once they say yes, you'll get
          another email with a link to set your password.
        </>
      }
    >
      <div className="pending-invite">
        <h2 className="pending-invite__heading">What happens next</h2>
        <ol className="pending-invite__steps">
          <li>
            Your parent opens the email + clicks the link. (They have 7 days.)
          </li>
          <li>
            They read the privacy notice + verify it's really them.
          </li>
          <li>
            They tick what you're allowed to do on 4CM.
          </li>
          <li>
            <strong>You'll get an email</strong> with a link to set your password.
            Then you can sign in.
          </li>
        </ol>

        <div className="pending-invite__hint">
          <strong>Didn't get the email?</strong> Check your parent's spam folder. If it's
          still missing after 10 minutes, you can resend it.
        </div>
      </div>
    </ConsentLayout>
  );
}

function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  if (local.length <= 2) return `${local[0]}*@${domain}`;
  return `${local[0]}${'*'.repeat(Math.max(1, local.length - 2))}${local[local.length - 1]}@${domain}`;
}
